import React, { useState, useEffect } from 'react';
import { FaCoins, FaSearch, FaTimes, FaSpinner, FaEdit, FaHistory, FaTrash, FaFileExcel } from 'react-icons/fa';
import empleadosApi from '../../services/api/empleadosApi';
import remuneracionesApi from '../../services/api/remuneracionesApi';
import { showSuccessToast, showErrorToast } from '../../services/utils/errorHandler';
import CargaMasivaRemuneraciones from './CargaMasivaRemuneraciones';
import useRrhhProyecto from '../../hooks/useRrhhProyecto';
import RrhhProyectoSelector from '../../components/rrhh/RrhhProyectoSelector';

const Remuneraciones = () => {
  const {
    isCorporativo,
    proyectoId,
    setProyectoId,
    proyectos,
    loadingProyectos,
    needsProyectoSelection,
  } = useRrhhProyecto();

  const [empleados, setEmpleados] = useState([]);
  const [empleadosSinAsignarRaw, setEmpleadosSinAsignarRaw] = useState([]);
  const [remuneraciones, setRemuneraciones] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchSinAsignar, setSearchSinAsignar] = useState('');
  const [searchAsignados, setSearchAsignados] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showCargaMasivaModal, setShowCargaMasivaModal] = useState(false);
  const [editEmpleado, setEditEmpleado] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [empleadoToDelete, setEmpleadoToDelete] = useState(null);
  const [montoAsignacionDefault, setMontoAsignacionDefault] = useState(0);

  // Form state
  const [form, setForm] = useState({
    sueldo: 0,
    sueldo_valor_dia: 0,
    sueldo_valor_dia_feriado: 0,
    sueldo_valor_hhee_25: 0,
    sueldo_valor_hhee_33: 0,
    asignacion_familiar: 0,
    asignacion_familiar_sustento: '',
    asignacion_familiar_valor: 0,
    bonificacion_fija_valor: 0,
    bonificacion_observacion: ''
  });
  const [fileToUpload, setFileToUpload] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchDatos = async (query = '') => {
    console.log('fetchDatos INICIO. proyectoId:', proyectoId, 'query:', query);
    if (!proyectoId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      console.log('Llamando a getEmpleadosSinRemuneracion...');
      const empSinAsig = await remuneracionesApi.getEmpleadosSinRemuneracion(proyectoId);
      console.log('getEmpleadosSinRemuneracion EXITOSO:', empSinAsig);
      setEmpleadosSinAsignarRaw(empSinAsig || []);

      console.log('Llamando a getEmpleados...');
      const empRes = await empleadosApi.getEmpleados(proyectoId, query);
      console.log('getEmpleados EXITOSO:', empRes.data);
      const emps = empRes.data || [];
      
      const asignadosIds = new Set(emps.map(e => e.id));
      (empSinAsig || []).forEach(e => asignadosIds.delete(e.id));
      const empsAsignados = emps.filter(e => asignadosIds.has(e.id));
      
      setEmpleados(empsAsignados);

      const remMap = {};
      console.log('Llamando a getActiva para', empsAsignados.length, 'empleados...');
      await Promise.all(
        empsAsignados.map(async (emp) => {
          try {
            const rem = await remuneracionesApi.getActiva(emp.id);
            if (rem) {
              remMap[emp.id] = rem;
            }
          } catch (e) {
            console.error('Error en getActiva para empleado', emp.id, e);
          }
        })
      );
      console.log('getActiva terminó. Remuneraciones map:', remMap);
      setRemuneraciones(remMap);

    } catch (err) {
      console.error('ERROR EN fetchDatos:', err);
      showErrorToast(err, 'Error cargando empleados');
    } finally {
      console.log('fetchDatos FIN. Apagando loading...');
      setLoading(false);
    }
  };

  // Buscar solo cuando cambie el search de asignados
  useEffect(() => {
    fetchDatos('');
    remuneracionesApi.getParametria('montoAsignacion')
      .then(res => { if (res && res.valor) setMontoAsignacionDefault(res.valor); })
      .catch(e => console.error('Error parametria:', e));
  }, [proyectoId]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => fetchDatos(searchAsignados), 400);
    return () => clearTimeout(t);
  }, [searchAsignados]); // eslint-disable-line

  const openEdit = (emp) => {
    setEditEmpleado(emp);
    const rem = remuneraciones[emp.id];
    if (rem) {
      setForm({
        sueldo: rem.sueldo || 0,
        sueldo_valor_dia: rem.sueldo_valor_dia || 0,
        sueldo_valor_dia_feriado: rem.sueldo_valor_dia_feriado || 0,
        sueldo_valor_hhee_25: rem.sueldo_valor_hhee_25 || 0,
        sueldo_valor_hhee_33: rem.sueldo_valor_hhee_33 || 0,
        asignacion_familiar: rem.asignacion_familiar || 0,
        asignacion_familiar_sustento: rem.asignacion_familiar_sustento || '',
        asignacion_familiar_valor: rem.asignacion_familiar_valor || montoAsignacionDefault,
        bonificacion_fija_valor: rem.bonificacion_fija_valor || 0,
        bonificacion_observacion: rem.bonificacion_observacion || ''
      });
    } else {
      setForm({
        sueldo: 0, sueldo_valor_dia: 0, sueldo_valor_dia_feriado: 0,
        sueldo_valor_hhee_25: 0, sueldo_valor_hhee_33: 0,
        asignacion_familiar: 0, asignacion_familiar_sustento: '', asignacion_familiar_valor: montoAsignacionDefault,
        bonificacion_fija_valor: 0, bonificacion_observacion: ''
      });
    }
    setFileToUpload(null);
    setShowModal(true);
  };

  const openHistory = async (emp) => {
    setEditEmpleado(emp);
    try {
      const hist = await remuneracionesApi.getHistorial(emp.id);
      setHistorial(hist || []);
      setShowHistory(true);
    } catch (err) {
      showErrorToast(err, 'Error cargando historial');
    }
  };

  const confirmDelete = (emp) => {
    setEmpleadoToDelete(emp);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!empleadoToDelete) return;
    try {
      await remuneracionesApi.deleteRemuneracion(empleadoToDelete.id);
      showSuccessToast('Remuneración desactivada');
      setShowDeleteConfirm(false);
      setEmpleadoToDelete(null);
      fetchDatos(searchAsignados); // refresca las grillas
    } catch (err) {
      showErrorToast(err, 'Error al eliminar');
    }
  };

  const handleSueldoChange = (e) => {
    const sueldoStr = e.target.value;
    const sueldo = parseFloat(sueldoStr) || 0;
    const valor_dia = sueldo / 30;
    const valor_dia_feriado = valor_dia * 2;
    const valor_hhee_25 = (valor_dia / 8) * 1.25;
    const valor_hhee_33 = (valor_dia / 8) * 1.33;

    setForm(prev => ({
      ...prev,
      sueldo: sueldoStr,
      sueldo_valor_dia: valor_dia.toFixed(2),
      sueldo_valor_dia_feriado: valor_dia_feriado.toFixed(2),
      sueldo_valor_hhee_25: valor_hhee_25.toFixed(2),
      sueldo_valor_hhee_33: valor_hhee_33.toFixed(2)
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let sustentoPath = form.asignacion_familiar_sustento;
      
      // Upload file si se seleccionó uno nuevo
      if (fileToUpload && form.asignacion_familiar == 1) {
        const uploadRes = await remuneracionesApi.uploadSustento(editEmpleado.id, fileToUpload);
        if (uploadRes.success) {
          sustentoPath = uploadRes.file_path;
        }
      }

      const payload = {
        empleado_id: editEmpleado.id,
        sueldo: Number(form.sueldo),
        sueldo_valor_dia: Number(form.sueldo_valor_dia),
        sueldo_valor_dia_feriado: Number(form.sueldo_valor_dia_feriado),
        sueldo_valor_hhee_25: Number(form.sueldo_valor_hhee_25),
        sueldo_valor_hhee_33: Number(form.sueldo_valor_hhee_33),
        asignacion_familiar: Number(form.asignacion_familiar),
        asignacion_familiar_sustento: sustentoPath,
        asignacion_familiar_valor: Number(form.asignacion_familiar_valor),
        bonificacion_fija_valor: Number(form.bonificacion_fija_valor),
        bonificacion_observacion: form.bonificacion_observacion ? form.bonificacion_observacion.trim().toUpperCase() : ''
      };

      await remuneracionesApi.crearRemuneracion(payload);
      showSuccessToast('Información financiera actualizada');
      setShowModal(false);
      fetchDatos(searchAsignados);
    } catch (err) {
      showErrorToast(err, 'Error al guardar datos');
    } finally {
      setSaving(false);
    }
  };

  // Contar alertas y aplicar filtro de búsqueda en el front a los Sin Asignar
  const qSinAsignar = searchSinAsignar.toLowerCase();
  const empleadosSinAsignar = empleadosSinAsignarRaw.filter(e => {
    if (!qSinAsignar) return true;
    const name = e.nombre_completo || `${e.nombre_1} ${e.apellido_1}` || '';
    const doc = e.numero_documento_identidad || '';
    return name.toLowerCase().includes(qSinAsignar) || doc.toLowerCase().includes(qSinAsignar);
  });
  
  const empleadosAsignados = empleados;

  return (
    <>
      <style>{`
        .dh-carga-bg { background: linear-gradient(135deg,#f8f9fa,#e9ecef); min-height:100vh; padding: 2rem; font-family: 'Inter', sans-serif; }
        .dh-card-main { border:none; border-radius:20px; box-shadow:0 15px 35px rgba(0,0,0,.1); background:#fff; overflow:hidden; }
        .dh-card-header { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:1.5rem 2rem; border:none; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .dh-header-title { color:#fff; font-weight:700; font-size:1.5rem; margin:0; display:flex; align-items:center; gap:12px; }
        .btn-dh-warning { background:linear-gradient(135deg,#f59e0b,#d97706); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(217,119,6,.3); }
        .btn-dh-primary { background:linear-gradient(135deg,#11998e,#38ef7d); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(17,153,142,.3); transition:all .3s; cursor:pointer; display:flex; align-items:center; gap:8px; }
        .btn-dh-primary:hover { transform:translateY(-2px); }
      `}</style>

      <div className="dh-carga-bg">
        <RrhhProyectoSelector
          isCorporativo={isCorporativo}
          proyectos={proyectos}
          proyectoId={proyectoId}
          onChange={setProyectoId}
          loading={loadingProyectos}
        />

        {needsProyectoSelection && (
          <div className="dh-alert dh-alert-info mb-3" style={{ borderRadius: 10 }}>
            Seleccione un proyecto para gestionar remuneraciones.
          </div>
        )}

        <div style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)',
                      color: '#fff', padding: '18px 24px', borderRadius: '16px',
                      marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <FaCoins style={{ marginRight: 8 }} size={28} />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>Remuneración de empleados</h2>
        </div>

        {empleadosSinAsignarRaw.length > 0 && (
          <div className="dh-card-main" style={{ marginBottom: '32px' }}>
            <div className="dh-card-header" style={{ background: 'linear-gradient(135deg, #e11d48, #9f1239)', padding: '12px 24px' }}>
              <h5 className="dh-header-title" style={{ fontSize: '18px' }}>Empleados por Completar ({empleadosSinAsignar.length})</h5>
              <div style={{ display: 'flex', gap: '10px', flex: 1, justifyContent: 'flex-end' }}>
                <button className="btn-dh-primary" onClick={() => setShowCargaMasivaModal(true)} style={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontSize: '0.85rem', padding: '8px 12px' }}>
                  <FaFileExcel /> Completar Masivo
                </button>
                <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                  <FaSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre o documento..."
                    value={searchSinAsignar}
                    onChange={e => setSearchSinAsignar(e.target.value)}
                    style={{ width: '100%', padding: '8px 14px 8px 40px', borderRadius: 8, border: 'none', outline: 'none', fontSize: '0.9rem' }}
                  />
                </div>
              </div>
            </div>
            
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#374151', width: '50px' }}>#</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Nombre Completo</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Documento</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Régimen</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 48 }}>
                        <FaSpinner className="fa-spin" style={{ fontSize: 28, color: '#db7a5b' }} />
                      </td>
                    </tr>
                  ) : empleadosSinAsignar.map((emp, idx) => (
                    <tr key={emp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: '#6b7280' }}>{idx + 1}</td>
                      <td style={{ padding: '11px 16px', fontWeight: 600 }}>{emp.nombre_completo || `${emp.nombre_1} ${emp.apellido_1}`}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: '#6b7280' }}>
                        <div style={{ fontSize: '10px', textTransform: 'uppercase' }}>{emp.tipo_documento_codigo || 'Documento'}</div>
                        <div style={{ fontWeight: 600 }}>{emp.numero_documento_identidad}</div>
                      </td>
                      <td style={{ padding: '11px 16px', textAlign: 'center', color: '#8b5cf6', fontWeight: 'bold' }}>{emp.regimen_empleado || emp.regimen_laboral?.nombre || '—'}</td>
                      <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                        <button onClick={() => openEdit(emp)} style={{ background: '#667eea', border: 'none', color: '#fff', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 'bold' }}>
                          <FaEdit size={14} /> Completar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="dh-card-main">
          <div className="dh-card-header" style={{ background: 'linear-gradient(135deg, #6366f1, #3730a3)', padding: '12px 24px' }}>
            <h5 className="dh-header-title" style={{ fontSize: '18px' }}>Empleados Asignados</h5>
            <div style={{ position: 'relative', flex: 1, maxWidth: 400, marginLeft: '24px' }}>
              <FaSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 }} />
              <input
                type="text"
                placeholder="Buscar por nombre o número de documento..."
                value={searchAsignados}
                onChange={e => setSearchAsignados(e.target.value)}
                style={{ width: '100%', padding: '8px 14px 8px 40px', borderRadius: 8, border: 'none', outline: 'none' }}
              />
            </div>
            <span style={{ background: '#fff', color: '#819dd3', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>{empleadosAsignados.length} empleados</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151', width: '30px' }}>#</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Nombre</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Documento</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Sueldo</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Sueldo Día</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Día Feriado</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>HHEE 25%</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>HHEE 33%</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Asig. Fam.</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Bonificación</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Activo</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#374151' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                      <FaSpinner className="fa-spin" style={{ fontSize: 28, color: '#819dd3' }} />
                    </td>
                  </tr>
                ) : empleadosAsignados.map((emp, idx) => {
                  const rem = remuneraciones[emp.id];
                  return (
                    <tr key={emp.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#6b7280' }}>{idx + 1}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{emp.nombre_1} {emp.apellido_1}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: '#6b7280' }}>
                        <div style={{ fontSize: '10px', textTransform: 'uppercase' }}>{emp.tipo_documento_codigo || 'Documento'}</div>
                        <div style={{ fontWeight: 600 }}>{emp.numero_documento_identidad}</div>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold', color: '#059669' }}>S/ {rem.sueldo}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>S/ {rem.sueldo_valor_dia}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>S/ {rem.sueldo_valor_dia_feriado}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>S/ {rem.sueldo_valor_hhee_25}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>S/ {rem.sueldo_valor_hhee_33}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>{rem.asignacion_familiar ? `Sí (S/ ${rem.asignacion_familiar_valor})` : 'No'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>S/ {rem.bonificacion_fija_valor}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        {rem.activo === 1 ? (
                          <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 'bold' }}>Sí</span>
                        ) : (
                          <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 'bold' }}>No</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button onClick={() => openEdit(emp)} title="Editar" style={{ background: '#667eea', border: 'none', color: '#fff', padding: '6px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                            <FaEdit size={12} />
                          </button>
                          <button onClick={() => openHistory(emp)} title="Ver Historial" style={{ background: '#6b7280', border: 'none', color: '#fff', padding: '6px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                            <FaHistory size={12} />
                          </button>
                          <button onClick={() => confirmDelete(emp)} title="Eliminar" style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '6px', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL DE EDICIÓN */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#4f46e5', padding: '16px 24px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Editar Remuneración {editEmpleado?.nombre_1} {editEmpleado?.apellido_1}</h5>
              <FaTimes style={{ cursor: 'pointer' }} onClick={() => setShowModal(false)} />
            </div>
            
            <form onSubmit={handleSave} style={{ padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Sueldo Base <span style={{ color: 'red' }}>*</span></label>
                  <input type="number" step="0.01" value={form.sueldo} onChange={handleSueldoChange} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6 }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Valor Día <span style={{ color: 'red' }}>*</span></label>
                  <input type="number" step="0.01" value={form.sueldo_valor_dia} readOnly style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, backgroundColor: '#f3f4f6' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Valor Día Feriado <span style={{ color: 'red' }}>*</span></label>
                  <input type="number" step="0.01" value={form.sueldo_valor_dia_feriado} readOnly style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, backgroundColor: '#f3f4f6' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Valor HHEE 25% <span style={{ color: 'red' }}>*</span></label>
                  <input type="number" step="0.01" value={form.sueldo_valor_hhee_25} readOnly style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, backgroundColor: '#f3f4f6' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Valor HHEE 33% <span style={{ color: 'red' }}>*</span></label>
                  <input type="number" step="0.01" value={form.sueldo_valor_hhee_33} readOnly style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6, backgroundColor: '#f3f4f6' }} required />
                </div>
              </div>

              <hr style={{ margin: '20px 0' }} />
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Asignación Familiar <span style={{ color: 'red' }}>*</span></label>
                  <select 
                    value={form.asignacion_familiar} 
                    onChange={e => {
                      const val = Number(e.target.value);
                      setForm(prev => ({
                        ...prev, 
                        asignacion_familiar: val,
                        asignacion_familiar_valor: val === 1 && prev.asignacion_familiar_valor == 0 ? montoAsignacionDefault : prev.asignacion_familiar_valor
                      }));
                    }} 
                    style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6 }} 
                    required
                  >
                    <option value={0}>No</option>
                    <option value={1}>Sí</option>
                  </select>
                </div>
                {form.asignacion_familiar == 1 && (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Valor Asignación <span style={{ color: 'red' }}>*</span></label>
                    <input type="number" step="0.01" value={form.asignacion_familiar_valor} onChange={e => setForm({...form, asignacion_familiar_valor: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6 }} required />
                  </div>
                )}
              </div>

              <hr style={{ margin: '20px 0' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Bonificación Fija Valor</label>
                  <input type="number" step="0.01" value={form.bonificacion_fija_valor} onChange={e => setForm({...form, bonificacion_fija_valor: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Observación Bonificación</label>
                  <input type="text" value={form.bonificacion_observacion} onChange={e => setForm({...form, bonificacion_observacion: e.target.value})} style={{ width: '100%', padding: 8, border: '1px solid #ccc', borderRadius: 6 }} />
                </div>
              </div>

              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={saving} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>
                  {saving ? 'Guardando...' : 'Guardar y Archivar Historial'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE HISTORIAL */}
      {showHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#6b7280', padding: '16px 24px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontWeight: 700 }}>Historial Remuneración - {editEmpleado?.nombre_1} {editEmpleado?.apellido_1}</h5>
              <FaTimes style={{ cursor: 'pointer' }} onClick={() => setShowHistory(false)} />
            </div>
            <div style={{ padding: 24, overflowX: 'auto' }}>
              {historial.length === 0 ? (
                <p style={{ color: '#6b7280', textAlign: 'center', margin: '20px 0' }}>No hay historial registrado.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Fecha/Hora</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Sueldo</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Asig. Fam.</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Bonif. Fija</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((h) => (
                      <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '10px 12px' }}>{new Date(h.created_at).toLocaleString()}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>S/ {h.sueldo}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>{h.asignacion_familiar ? `Sí (S/ ${h.asignacion_familiar_valor})` : 'No'}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>S/ {h.bonificacion_fija_valor}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {h.activo === 1 ? (
                            <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 'bold' }}>Activo</span>
                          ) : (
                            <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>Archivado</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* MODAL CONFIRMAR ELIMINACIÓN */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 400, padding: 24, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <div style={{ background: '#fee2e2', color: '#ef4444', width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FaTrash size={24} />
            </div>
            <h3 style={{ margin: '0 0 8px', fontSize: 20, color: '#1f2937' }}>¿Eliminar Remuneración?</h3>
            <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: 14 }}>
              Estás a punto de desactivar la información financiera de <strong>{empleadoToDelete?.nombre_1} {empleadoToDelete?.apellido_1}</strong>. El empleado volverá a la lista de "Por Completar". ¿Deseas continuar?
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      <CargaMasivaRemuneraciones
        show={showCargaMasivaModal}
        onClose={() => setShowCargaMasivaModal(false)}
        onSaved={() => {
          setShowCargaMasivaModal(false);
          fetchDatos(searchAsignados);
        }}
        empleadosSinAsignar={empleadosSinAsignarRaw}
        montoAsignacionDefault={montoAsignacionDefault}
        proyectoId={proyectoId}
      />
    </>
  );
};

export default Remuneraciones;
